<template>
  <section class="usage-audit">
    <header>
      <h1>Usage Audit</h1>
    </header>

    <form class="export-form" @submit.prevent="exportCsv">
      <div class="date-range">
        <label>Date range</label>
        <div class="range">
          <div class="date-field">
            <label for="usage-audit-from">From</label>
            <div class="picker">
              <input
                id="usage-audit-from"
                class="display"
                type="text"
                placeholder="MM/DD/YYYY"
                maxlength="10"
                :value="formatDisplay(range.from)"
                @blur="onDisplayBlur('from', $event)"
                @keydown.enter.prevent="onDisplayBlur('from', $event)"
              />
              <input
                ref="fromPicker"
                class="native"
                type="date"
                tabindex="-1"
                :value="range.from"
                :max="fromMax"
                aria-hidden="true"
                @input="onPickerInput('from', $event.target.value)"
              />
              <button type="button" class="calendar-button" aria-label="Pick from date" @click="openPicker('fromPicker')">
                <p-icon name="calendar-blank-outline" size="sm" />
              </button>
            </div>
          </div>
          <span class="separator">–</span>
          <div class="date-field">
            <label for="usage-audit-to">To</label>
            <div class="picker">
              <input
                id="usage-audit-to"
                class="display"
                type="text"
                placeholder="MM/DD/YYYY"
                maxlength="10"
                :value="formatDisplay(range.to)"
                @blur="onDisplayBlur('to', $event)"
                @keydown.enter.prevent="onDisplayBlur('to', $event)"
              />
              <input
                ref="toPicker"
                class="native"
                type="date"
                tabindex="-1"
                :value="range.to"
                :min="range.from"
                :max="today"
                aria-hidden="true"
                @input="onPickerInput('to', $event.target.value)"
              />
              <button type="button" class="calendar-button" aria-label="Pick to date" @click="openPicker('toPicker')">
                <p-icon name="calendar-blank-outline" size="sm" />
              </button>
            </div>
          </div>
        </div>
      </div>
      <p v-if="validationError" class="error">{{ validationError }}</p>
      <p v-if="exportError" class="error">{{ exportError }}</p>
      <div class="actions">
        <p-button color="primary" type="submit" :disabled="exportPending || !canExport">
          {{ exportPending ? 'Exporting…' : 'Export CSV' }}
        </p-button>
      </div>
    </form>
  </section>
</template>

<script>
import { endOfDay, isValid, parse, parseISO, startOfDay } from 'date-fns';
import format from 'date-fns/fp/format/index.js';

import Button from '@/components/common/Button';
import Icon from '@/components/common/MdIcon';

const MAX_EXPORT_RANGE_MS = 90 * 24 * 60 * 60 * 1000;

export default {
  components: {
    'p-button': Button,
    'p-icon': Icon
  },
  data() {
    return {
      range: { from: '', to: '' },
      exportPending: false,
      exportError: '',
      validationError: ''
    };
  },
  computed: {
    canExport() {
      return Boolean(this.range?.from && this.range?.to);
    },
    today() {
      return format('yyyy-MM-dd', new Date());
    },
    fromMax() {
      return this.range?.to && this.range.to < this.today ? this.range.to : this.today;
    }
  },
  methods: {
    formatDisplay(isoDate) {
      if (!isoDate) {
        return '';
      }
      const parsed = parseISO(isoDate);
      return isValid(parsed) ? format('MM/dd/yyyy', parsed) : '';
    },
    onPickerInput(field, value) {
      this.range = { ...this.range, [field]: value || '' };
      this.validationError = '';
    },
    openPicker(ref) {
      const input = this.$refs[ref];
      if (!input) {
        return;
      }
      if (input.showPicker) {
        input.showPicker();
      } else {
        input.focus();
      }
    },
    onDisplayBlur(field, event) {
      const typed = (event.target.value || '').trim();
      if (!typed) {
        this.range = { ...this.range, [field]: '' };
        event.target.value = '';
        return;
      }

      const digits = typed.replace(/\D/g, '');
      const parsed = digits.length === 8 ? parse(digits, 'MMddyyyy', new Date()) : null;
      if (!parsed || !isValid(parsed)) {
        event.target.value = this.formatDisplay(this.range[field]);
        this.validationError = 'Enter valid dates as MM/DD/YYYY';
        return;
      }

      this.range = { ...this.range, [field]: format('yyyy-MM-dd', parsed) };
      this.validationError = '';
      event.target.value = this.formatDisplay(this.range[field]);
    },
    validateRange() {
      this.validationError = '';
      if (!this.range?.from || !this.range?.to) {
        this.validationError = 'Both from and to dates are required';
        return null;
      }

      const from = startOfDay(parseISO(this.range.from));
      const to = endOfDay(parseISO(this.range.to));
      if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) {
        this.validationError = 'Enter valid dates as MM/DD/YYYY';
        return null;
      }
      if (this.range.from > this.today || this.range.to > this.today) {
        this.validationError = 'Dates cannot be in the future';
        return null;
      }
      if (to < from) {
        this.validationError = 'To date must be on or after from date';
        return null;
      }
      if (to.getTime() - from.getTime() > MAX_EXPORT_RANGE_MS) {
        this.validationError = 'Date range cannot exceed 90 days';
        return null;
      }
      return { from, to };
    },
    filenameFromHeader(headerValue) {
      if (!headerValue) {
        return null;
      }
      const match = /filename="?([^"]+)"?/i.exec(headerValue);
      return match?.[1] || null;
    },
    defaultFilename() {
      return `nlpUsage_${format('yyyy-MM-dd_HH-mm-ss', new Date())}.csv`;
    },
    async exportCsv() {
      this.exportError = '';
      const dates = this.validateRange();
      if (!dates) {
        return;
      }

      const params = new URLSearchParams({
        from: dates.from.toISOString(),
        to: dates.to.toISOString()
      });
      const token = localStorage.getItem('PATENT_HUB_AUTH_TOKEN');
      this.exportPending = true;
      try {
        const response = await fetch(`/api/v2/nlp/usage-audit/export?${params.toString()}`, {
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
            'user-time-zone': Intl.DateTimeFormat().resolvedOptions().timeZone,
            Accept: 'text/csv'
          }
        });

        if (!response.ok) {
          let message = `Export failed (${response.status})`;
          try {
            const payload = await response.json();
            if (payload?.message) {
              message = Array.isArray(payload.message) ? payload.message.join(', ') : payload.message;
            }
          } catch (_) {
            // non-JSON error body
          }
          throw new Error(message);
        }

        const blob = await response.blob();
        const filename =
          this.filenameFromHeader(response.headers.get('Content-Disposition')) || this.defaultFilename();
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        link.click();
        URL.revokeObjectURL(link.href);
      } catch (error) {
        this.exportError = error.message || 'Failed to export usage audit';
      } finally {
        this.exportPending = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.usage-audit {
  width: 100%;
  padding: 0 0.5rem 0 1rem;
  box-sizing: border-box;

  header {
    padding: 0.5rem 0.5rem 0;
    h1 {
      color: var(--theme-on-surface);
      font-size: 1.5rem;
      font-weight: 700;
    }
  }

  .export-form {
    max-width: 480px;
    padding: 1.5rem 0.5rem;
    display: grid;
    grid-gap: 1rem;
  }

  .date-range {
    display: grid;
    grid-gap: 5px;

    > label {
      font-weight: 500;
      font-size: 0.75rem;
      letter-spacing: 0.025em;
    }

    .range {
      display: grid;
      grid-template-columns: 1fr max-content 1fr;
      grid-gap: 10px;
      align-items: end;
    }

    .separator {
      padding-bottom: 8px;
      color: var(--theme-on-surface);
    }
  }

  .date-field {
    display: grid;
    grid-gap: 5px;
    min-width: 0;

    label {
      font-weight: 500;
      font-size: 0.75rem;
      letter-spacing: 0.025em;
    }
  }

  .picker {
    position: relative;
    min-width: 0;
  }

  .display {
    width: 100%;
    box-sizing: border-box;
    padding: 8px 36px 8px 12px;
    font-size: 0.85rem;
    font-weight: 400;
    border-radius: 2px;
    border: 1px solid var(--theme-on-background-dark);
    color: var(--theme-on-background-dark);
    background: var(--theme-background);
    outline: none;
    font-family: inherit;
    min-height: 36px;
  }

  .native {
    position: absolute;
    right: 8px;
    bottom: 0;
    width: 1px;
    height: 1px;
    padding: 0;
    border: none;
    background: transparent;
    opacity: 0;
    pointer-events: none;
  }

  .calendar-button {
    position: absolute;
    top: 0;
    right: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 100%;
    padding: 0;
    border: none;
    background: transparent;
    color: var(--theme-on-background-dark);
    cursor: pointer;
    transition: color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;

    &:hover,
    &:focus {
      color: var(--theme-on-background);
      outline: none;
    }
  }

  .display {
    transition: background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
      border 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;

    &:hover {
      border-color: var(--theme-on-background);
      color: var(--theme-on-background);
    }

    &:focus,
    &:active {
      border-color: var(--theme-on-background);
      color: var(--theme-on-background);
      outline: none;
    }
  }

  .actions {
    display: flex;
    justify-content: flex-start;
  }

  .error {
    margin: 0;
    color: var(--theme-error);
    font-size: 0.85rem;
  }
}
</style>
